/* Root's per-approver detail under a project card (DESIGN29 → DESIGN49).

   The strip above says WHICH stage the project is at; this block says WHO
   it is waiting for, without opening the project. Since DESIGN49 it is a
   ONE-LINE summary of the stage that matters right now («Proposal ·
   awaiting Dan, Dana · 0 of 2», «Estimate · AI estimating…», «Proposal ·
   approved by Ameer») with the full per-stage history folded underneath —
   the owner: «можно было раскрывать доп. информацию, а не как сейчас
   открыто».

   Rendered ONLY when the caller received the root-only overview — a
   non-root account never gets the data, so this component never mounts for
   them and the cards look exactly as they always did. */

import React from 'react';
import { Check, ChevronDown, Clock3 } from 'lucide-react';
import { ProjectOverview, StageDetail, PricingStageDetail as PricingStage } from './api';
import { fmtDate } from './utils';

const dt = (iso: string | null | undefined) => fmtDate(iso);

type Tone = 'done' | 'wait' | 'live';

interface Summary {
  label: string;
  tone: Tone;
  text: string;
  /** A muted time after the text (done stages). */
  when?: string;
  count?: string;
}

/** The one line worth reading: the first stage still in motion, or — when
    everything is done — the last stage that finished. */
export function summarize(d: ProjectOverview): Summary | null {
  const stages: Array<[string, StageDetail | PricingStage, string]> = [
    ['Architecture', d.architecture, 'AI drawing…'],
    ['Estimate', d.estimate, 'AI estimating…'],
    ['Pricing', d.pricing, ''],
    ['Proposal', d.proposal, 'AI writing…'],
  ];
  let lastDone: Summary | null = null;
  for (const [label, s, aiLine] of stages) {
    if (s.state === 'idle') continue;
    if (s.state === 'generating') return { label, tone: 'live', text: aiLine };
    if (s.state === 'awaiting') {
      if ('waiting' in s) {
        const who = s.waiting.length
          ? 'awaiting ' + s.waiting.join(', ')
          : s.voted.length ? 'awaiting approval' : 'awaiting any approver';
        return {
          label, tone: 'wait', text: who,
          count: s.required > 1 ? `${s.given} of ${s.required}` : undefined,
        };
      }
      return { label, tone: 'wait', text: (s as PricingStage).assigned_to_name
        ? 'awaiting ' + (s as PricingStage).assigned_to_name + ' to approve'
        : 'awaiting pricing approval' };
    }
    // approved / done
    const by = 'approved_by' in s ? s.approved_by : (s as PricingStage).by;
    const at = 'approved_at' in s ? s.approved_at : (s as PricingStage).at;
    lastDone = {
      label, tone: 'done',
      text: 'approved' + (by ? ' by ' + by : ''),
      when: at ? dt(at) : undefined,
    };
  }
  return lastDone;
}

/** One approval stage as a compact line. Idle stages render nothing — the
    detail block only speaks about work that exists. */
function StageLine({ label, d, aiLine }: { label: string; d: StageDetail; aiLine: string }) {
  if (d.state === 'idle') return null;

  if (d.state === 'generating') {
    return (
      <div className="arch-apprv-row">
        <span className="arch-apprv-stage">{label}</span>
        <span className="arch-apprv-live">
          <span className="arch-spinner arch-apprv-spinner" />
          {aiLine}
        </span>
      </div>
    );
  }

  if (d.state === 'approved') {
    return (
      <div className="arch-apprv-row">
        <span className="arch-apprv-stage">{label}</span>
        <span className="arch-apprv-done">
          <span className="arch-apprv-tick" aria-hidden="true"><Check size={10} strokeWidth={2.3} /></span>
          {d.approved_by || 'approved'}
          {d.approved_at ? <span className="arch-apprv-when">{dt(d.approved_at)}</span> : null}
        </span>
      </div>
    );
  }

  // awaiting — who voted, who is missing. An unnamed policy (production's
  // default) words itself as «any approver» instead of naming nobody.
  return (
    <div className="arch-apprv-row">
      <span className="arch-apprv-stage">{label}</span>
      <span className="arch-apprv-wait">
        {d.voted.map((v, i) => (
          <span key={'v' + i} className="arch-apprv-voted">
            <span className="arch-apprv-tick" aria-hidden="true"><Check size={10} strokeWidth={2.3} /></span>{v.name || 'approved'}
          </span>
        ))}
        {d.waiting.length ? (
          <span className="arch-apprv-pending">
            {d.turn ? 'next: ' + d.turn : 'awaiting ' + d.waiting.join(', ')}
          </span>
        ) : (
          <span className="arch-apprv-pending">
            {d.voted.length ? 'awaiting approval' : 'awaiting any approver'}
          </span>
        )}
        {d.required > 1 ? (
          <span className="arch-apprv-count">{d.given} of {d.required}</span>
        ) : null}
      </span>
    </div>
  );
}

interface Props {
  d: ProjectOverview;
  open: boolean;
  onToggle: () => void;
}

export default function ApproversDetail({ d, open, onToggle }: Props) {
  const sum = summarize(d);
  if (!sum) return null;

  const rows = [
    <StageLine key="a" label="Architecture" d={d.architecture} aiLine="AI drawing…" />,
    <StageLine key="e" label="Estimate" d={d.estimate} aiLine="AI estimating…" />,
  ];

  const pricing =
    d.pricing.state === 'idle' ? null : (
      <div className="arch-apprv-row" key="p">
        <span className="arch-apprv-stage">Pricing</span>
        {d.pricing.state === 'done' ? (
          <span className="arch-apprv-done">
            <span className="arch-apprv-tick" aria-hidden="true"><Check size={10} strokeWidth={2.3} /></span>
            {d.pricing.by || 'approved'}
            {d.pricing.at ? <span className="arch-apprv-when">{dt(d.pricing.at)}</span> : null}
          </span>
        ) : (
          <span className="arch-apprv-wait">
            <span className="arch-apprv-pending">{d.pricing.assigned_to_name ? 'awaiting ' + d.pricing.assigned_to_name + ' to approve' : 'awaiting pricing approval'}</span>
          </span>
        )}
      </div>
    );

  const all = [
    ...rows,
    pricing,
    <StageLine key="pr" label="Proposal" d={d.proposal} aiLine="AI writing…" />,
  ].filter(Boolean);

  return (
    <div className={'arch-apprv' + (open ? ' open' : '')}>
      <button
        type="button"
        className="arch-apprv-sum"
        aria-expanded={open}
        title={open ? 'Hide the approval trail' : 'Show who approved what, and when'}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <span className={'arch-apprv-dot ' + sum.tone} aria-hidden="true">
          {sum.tone === 'live' ? <span className="arch-spinner arch-apprv-spinner" />
            : sum.tone === 'done' ? <Check size={13} strokeWidth={2} /> : <Clock3 size={13} strokeWidth={1.8} />}
        </span>
        <span className="arch-apprv-sum-copy">
          <span className="arch-apprv-sum-stage">{sum.label}</span>
          <span className={'arch-apprv-sum-text ' + sum.tone}>
            {sum.text}
            {sum.when ? <span className="arch-apprv-when">{sum.when}</span> : null}
          </span>
        </span>
        {sum.count ? <span className="arch-apprv-count">{sum.count}</span> : null}
        <ChevronDown className="arch-apprv-chevron" size={14} aria-hidden="true" />
      </button>
      <div className={'arch-apprv-body' + (open ? ' open' : '')} aria-hidden={!open}>
        <div className="arch-apprv-rows">{all}</div>
      </div>
    </div>
  );
}
